import { queryCalendar } from '@/api/calendar-api';
import type { CalendarQueryInput } from '@vivport/backend/types';
import { useQuery } from '@tanstack/react-query';

function toStableQueryKeyInput(input: CalendarQueryInput | null) {
  if (!input) {
    return null;
  }

  return {
    rangeStart: input.rangeStart,
    rangeEnd: input.rangeEnd,
    showHubClashes: input.showHubClashes,
    pageSize: input.pageSize,
    cursorStart: input.cursor?.start ?? null,
    cursorEventId: input.cursor?.eventId ?? null,
    sources: input.sources
      .map((source) => ({
        kind: source.kind,
        memberId: source.memberId,
        isPrimary: source.isPrimary,
        providerSelector: source.providerSelector
          ? {
            provider: source.providerSelector.provider,
            memberExternalAccountId: source.providerSelector.memberExternalAccountId,
            providerCalendarId: source.providerSelector.providerCalendarId,
          }
          : null,
      }))
      .sort((left, right) => {
        if (left.memberId !== right.memberId) {
          return left.memberId - right.memberId;
        }
        if (left.isPrimary === right.isPrimary) {
          if (!left.providerSelector && !right.providerSelector) {
            return 0;
          }
          if (!left.providerSelector) {
            return -1;
          }
          if (!right.providerSelector) {
            return 1;
          }
          if (left.providerSelector.memberExternalAccountId !== right.providerSelector.memberExternalAccountId) {
            return left.providerSelector.memberExternalAccountId - right.providerSelector.memberExternalAccountId;
          }
          return left.providerSelector.providerCalendarId.localeCompare(right.providerSelector.providerCalendarId);
        }
        return left.isPrimary ? -1 : 1;
      }),
  };
}

export const calendarKeys = {
  all: ['calendar'] as const,
  queries: () => [...calendarKeys.all, 'query'] as const,
  query: (input: CalendarQueryInput | null) => [...calendarKeys.queries(), toStableQueryKeyInput(input)] as const,
};

export const useCalendarQuery = (input: CalendarQueryInput | null) => {
  const hasSources = (input?.sources.length ?? 0) > 0;
  return useQuery({
    queryKey: calendarKeys.query(input),
    queryFn: () => queryCalendar(input!),
    enabled: !!input && hasSources,
  });
};
